import { useEffect, useState, useCallback } from 'react';

type CSSSelector = keyof HTMLElementTagNameMap | string;
type DimensionType = keyof Omit<DOMRect, 'toJSON' | 'x' | 'y'>;

interface UseElementDimensionOptions {
    selector: CSSSelector;
    dimension: DimensionType;
    observeResize?: boolean;
}

export const useElementDimensionBySelector = ({
    selector,
    dimension,
    observeResize = true,
}: UseElementDimensionOptions) => {
    const [elementDimension, setElementDimension] = useState<string | undefined>(undefined);

    const updateElementDimension = useCallback(() => {
        const element = document.querySelector(selector);
        if (element instanceof HTMLElement) {
            const rect = element.getBoundingClientRect();
            const value = rect[dimension];
            setElementDimension(`${value}px`);
        } else {
            setElementDimension(undefined);
        }
    }, [selector, dimension]);

    useEffect(() => {
        const element = document.querySelector(selector);
        if (!(element instanceof HTMLElement)) return;

        updateElementDimension();

        if (observeResize) {
            const resizeObserver = new ResizeObserver(updateElementDimension);
            resizeObserver.observe(element);

            return () => {
                resizeObserver.disconnect();
            };
        }
    }, [selector, dimension, observeResize, updateElementDimension]);

    return elementDimension;
};
